import {
  BulkCreateHeadcountRequestDocument,
  BulkDeleteHeadcountRequestDocument,
  BulkUpdateHeadcountRequestDocument,
  HeadcountRequestsDocument,
  HeadcountSummariesDocument,
  type AtsContractTypesEnum,
  type AtsJobPostingSalaryPeriodEnum,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface UseHeadcountRequestMutationsParams {
  periodId: string
  planningId?: string
  submissionPeriodId?: string
  companyId?: string
}

const useHeadcountRequestMutations = ({
  periodId,
  planningId,
  submissionPeriodId,
  companyId,
}: UseHeadcountRequestMutationsParams) => {
  const [bulkCreateMutation] = useMutation(BulkCreateHeadcountRequestDocument, {
    extractorFn: (data) => data.planningMutations.bulkCreatePlanningRequest,
  })

  const [bulkUpdateMutation] = useMutation(BulkUpdateHeadcountRequestDocument, {
    extractorFn: (data) => data.planningMutations.bulkUpdatePlanningRequest,
  })

  const [bulkDeleteMutation] = useMutation(BulkDeleteHeadcountRequestDocument, {
    extractorFn: (data) => data.planningMutations.bulkDeletePlanningRequest,
  })

  const showError = () => {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('common.save_error'),
      time: 2000,
    })
  }

  const bulkCreate = async ({
    managerId,
    locationId,
    jobCatalogTreeNodeUuid,
    oldJobCatalogLevelId,
    salary,
    workingTime,
  }: {
    managerId?: string
    locationId?: string
    jobCatalogTreeNodeUuid?: string
    oldJobCatalogLevelId?: string
    salary?: number
    workingTime?: number
  } = {}) => {
    if (!companyId) {
      return { success: false }
    }

    const result = await bulkCreateMutation({
      variables: {
        items: [
          {
            companyId,
            planningPeriodId: periodId,
            planningPlanningId: planningId ?? null,
            planningSubmissionPeriodId: submissionPeriodId ?? null,
            requestType: 'headcount',
            status: 'draft',
            details: {
              jobCatalogTreeNodeUuid: jobCatalogTreeNodeUuid ?? null,
              oldJobCatalogLevelId: oldJobCatalogLevelId ?? null,
              locationId: locationId ?? null,
              managerId: managerId ?? null,
              salary: salary != null ? salary * 100 : null,
              workingTime: workingTime != null ? String(workingTime) : null,
              startDate: null,
            },
          },
        ],
      },
      refetchQueries: [HeadcountSummariesDocument, HeadcountRequestsDocument],
    })

    const handled = handleResult(result, () => undefined)
    if (!handled.success) {
      showError()
    }

    return { success: handled.success }
  }

  const bulkUpdate = async (update: {
    id: string
    jobCatalogTreeNodeUuid?: string
    oldJobCatalogLevelId?: string
    locationId?: string
    managerId?: string
    salary?: number
    workingTime?: number
    startDate?: string
    currency?: string
    contractType?: AtsContractTypesEnum
    salaryFrequency?: AtsJobPostingSalaryPeriodEnum
  }) => {
    const result = await bulkUpdateMutation({
      variables: {
        updates: [
          {
            id: update.id,
            headcountDetails: {
              ...(update.jobCatalogTreeNodeUuid
                ? { jobCatalogTreeNodeUuid: update.jobCatalogTreeNodeUuid }
                : {}),
              ...(update.oldJobCatalogLevelId
                ? { oldJobCatalogLevelId: update.oldJobCatalogLevelId }
                : {}),
              ...(update.locationId ? { locationId: update.locationId } : {}),
              ...(update.managerId ? { managerId: update.managerId } : {}),
              ...(update.salary ? { salary: update.salary * 100, currency: update.currency } : {}),
              ...(update.workingTime != null ? { workingTime: String(update.workingTime) } : {}),
              ...(update.startDate ? { startDate: update.startDate } : {}),
              ...(update.contractType ? { contractType: update.contractType } : {}),
              ...(update.salaryFrequency ? { salaryFrequency: update.salaryFrequency } : {}),
            },
          },
        ],
      },
      refetchQueries: [HeadcountSummariesDocument],
    })

    const handled = handleResult(result, () => undefined)

    if (!handled.success) {
      showError()
    }

    return { success: handled.success }
  }

  const bulkDelete = async (ids: string[]) => {
    const result = await bulkDeleteMutation({
      variables: { ids },
      refetchQueries: [HeadcountSummariesDocument, HeadcountRequestsDocument],
    })

    const handled = handleResult(result, () => undefined)
    if (!handled.success) {
      showError()
    }

    return { success: handled.success }
  }

  return { bulkCreate, bulkUpdate, bulkDelete }
}

export default useHeadcountRequestMutations
